import { theme, type ThemeConfig } from 'antd';

export type ThemeMode = 'light' | 'dark';

/** Chrome heights the layout needs in both CSS and JS, so they live in one place. */
/**
 * The sidebar's left rail. `gutter` is where every label, icon and the logo
 * begin; `railInset` is how far the hover/selected pill is held off the edge,
 * so the pill has air around it without the text moving.
 *
 * Shared with the admin panel: the two sit side by side on the same desk often
 * enough that a different rail would read as a different product.
 */
export const NAV = { gutter: 16, railInset: 8 } as const;

/*
 * Narrower sider than the admin panel, and the one place the two deliberately
 * disagree.
 *
 * Everything else in this file is shared, because the two sit side by side on
 * the same desk. But width here is not a taste call: the purchase grid needs
 * 1180px before it starts scrolling sideways and hiding columns under its
 * pinned Amount. On a 1440px display, 1440 − 200 sider − 40 padding = 1200,
 * which clears it; at the admin's 240 it does not. Every 8px given back to the
 * sider is 8px taken off the widest table in the product.
 *
 * The admin panel can afford the extra 40px because its longest labels —
 * "Manufacturers", "Delivery & fees" — are worth more to it than any one table,
 * and its tables scroll inside their own container. This one's does not.
 */
export const LAYOUT = {
  headerHeight: 48,
  siderWidth: 200,
  siderCollapsedWidth: 64,
  /*
   * Capping lower meant a 1920 monitor showed the same cramped grid as a laptop
   * while leaving 300px of empty page either side.
   */
  contentMaxWidth: 1440,
} as const;

/**
 * The logo, sampled. `public/logo.png` is the source of truth for these — if the
 * mark is ever redrawn, re-sample rather than nudge by eye.
 *
 * The cross is two greens over a pale leaf, and the wordmark is set in an
 * almost-black green rather than in black.
 *
 * The neutrals below borrow nothing from it. They are the plain Polaris ramp — see the note
 * there for why tinting them toward the mark was a mistake.
 */
const BRAND = {
  /** The dark arm of the cross. The accent, and the colour of every primary action. */
  forest: '#16593f',
  /** Where the two arms overlap. */
  forestDeep: '#144934',
  /** The light arm. */
  sage: '#6eaf76',
  /** The leaf highlight. */
  mint: '#b6d9b8',
  /** The wordmark. Hue 161°, which is where the greys below get their cast. */
  ink: '#17342b',
} as const;

/**
 * Neutrals: zinc, cool, and carried further at both ends than Tailwind takes it.
 *
 * Polaris's ramp was here briefly and came back out. Its greys are perfectly
 * neutral - equal parts red, green and blue - and against this product's chrome
 * they read slightly warm and flat, the way an unmanaged screenshot does. Zinc
 * carries a few points of blue, which is what makes a grey look deliberate
 * rather than absent.
 *
 * What is kept from Polaris is the SPACING, which is the part that was doing
 * real work: the light end is placed so a white card separates from the page
 * (1.13:1, where Tailwind's own zinc-50 gave 1.04 and the card dissolved), and
 * the dark end carries on past zinc-950 so a dark card, a dark page and a
 * darker sider are three distinguishable surfaces instead of two.
 *
 * Every step below is referenced by exactly one role, and the half-steps exist
 * because a measurement demanded them - see the notes at each.
 *
 * The green appears exactly twice: a primary action, and the focus ring.
 */
const neutral = {
  0: '#ffffff',
  50: '#fafafa',
  /* Light page. 1.13:1 under a white card - the step at which an edge
     registers without the card needing a heavy border. */
  100: '#f1f1f3',
  200: '#e4e4e7',
  300: '#d4d4d8',
  400: '#a1a1aa',
  500: '#8a8a94',
  /*
   * The control edge, and the one value that serves BOTH themes.
   *
   * 4.83:1 on white and 3.59:1 on the dark card, so it clears the 3:1 that
   * WCAG 1.4.11 asks of the boundary identifying a control, either way round.
   * Containers use `200`/`800`; those are decorative and allowed to be faint.
   */
  600: '#71717a',
  /*
   * Light-mode tertiary text, and a step the contrast gate forced into
   * existence. Muted text outside a card sits on the PAGE, not on white, and
   * checking only against white is how that gets missed. 6.62:1 on white,
   * 5.87:1 on the page.
   */
  675: '#5c5c65',
  700: '#52525b',
  800: '#3f3f46',
  900: '#27272a',
  /*
   * Below here the ramp is ours. Tailwind stops at 950 and this product needs
   * three more surfaces under it: a popover above the card, a page below it,
   * and a sider below that.
   */
  925: '#242429',
  950: '#1a1a1e',
  /* The dark sider: between the card and the page. */
  960: '#141417',
  975: '#0f0f12',
  1000: '#09090b',
} as const;

/**
 * The accent, straight off the logo.
 *
 * Light mode is the mark's own forest green, which carries white at 8.3:1 — the
 * brand colour and the accessible one turn out to be the same colour, which is
 * luck worth taking. On a near-black surface that same green is a smudge, so
 * dark mode lifts it to the point where a white label still clears AA (5.3:1)
 * and the filled shape still separates from the background.
 *
 * A green brand next to a green success tag is a real tension, and it is
 * resolved by weight and by placement: the accent is dark and desaturated and
 * appears only on a filled control, a link, or the focus ring, while `success`
 * below is brighter and only ever appears as a small tag. Nothing else in the
 * chrome is green at all — hovers and selections are grey. The signals
 * that actually demand attention here — expiring, expired, out of stock — are
 * amber and red, and neither is anywhere near either green.
 */
const ACCENT = { light: BRAND.forest, dark: '#1f7a55' } as const;

/**
 * Type, in three jobs.
 *
 * A stack, a fallback chain and a `tnum` figure set are the only things that
 * actually matter here; which faces fill the roles is a taste call, so both
 * candidate pairings are written out and `PAIRING` picks one. Changing that one
 * word swaps the whole product, but `index.html` now requests ONLY the faces
 * the chosen pairing uses — Parkinsans and Inter were costing two font
 * downloads a page load to sit unused — so a swap back means editing the
 * Google Fonts URL too.
 *
 *   display — page titles and large figures. Allowed to have a voice.
 *   body    — everything else: labels, tables, forms. Wants to disappear.
 *   mono    — money columns, SKUs, batch and order numbers. See `.tabular`.
 *
 * The mono role is the one worth keeping whichever pairing wins. A proportional
 * face with `tnum` lines figures up, but it cannot make a column of SKUs and
 * batch numbers scannable the way a true monospace does, and this product is
 * mostly columns of exactly that.
 */
const SYSTEM =
  "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif";

const PAIRINGS = {
  /** Parkinsans has the geometry and the wide apertures; Inter carries density. */
  parkinsans: {
    display: `'Parkinsans', ${SYSTEM}`,
    body: `'Inter', ${SYSTEM}`,
  },
  /** Drawn as a family, so the headings and the tables agree by construction. */
  geist: {
    display: `'Geist', ${SYSTEM}`,
    body: `'Geist', ${SYSTEM}`,
  },
} as const;

/**
 * Geist, for both roles.
 *
 * Parkinsans gave the display line a voice, and a counter screen does not want
 * one: at 34px over a column of figures the wide apertures read as decorative
 * rather than clear. Geist is drawn as a family, so a 34px total and the 13px
 * table under it agree by construction, and its figures are properly tabular -
 * which matters more here than character, because most of what this product
 * sets in large type is money.
 */
const PAIRING: keyof typeof PAIRINGS = 'geist';

export const FONT = {
  display: PAIRINGS[PAIRING].display,
  body: PAIRINGS[PAIRING].body,
  mono: "'Geist Mono', ui-monospace, SFMono-Regular, 'SF Mono', Menlo, Consolas, monospace",
} as const;

/**
 * Status, as FILLS - a solid control with a white label on it.
 *
 * Identical in both themes, which is the point: a colour that carries white at
 * 4.5:1 does so whatever is behind the control. Re-measured against that label,
 * three of the four originals failed:
 *
 *     success  #16a34a  3.30:1  ->  #047b5d  5.25:1
 *     warning  #d97706  3.19:1  ->  #b45309  5.02:1
 *     error    #dc2626  4.83:1  ->  #c70a24  6.00:1
 *
 * The error swap is the smallest and still worth making: #dc2626 cleared AA by
 * 0.33 and left nothing for a hover state to spend.
 */
const STATUS = {
  success: '#047b5d',
  warning: '#b45309',
  error: '#c70a24',
  info: '#005bd3',
} as const;

/**
 * Status, as INK - the same five meanings written as text.
 *
 * A separate set, and it has to be. A colour dark enough to carry a white label
 * has a luminance around 0.18, which against the dark card leaves about 3.8:1 -
 * so one value cannot be both a fill and readable text in dark mode. The
 * arithmetic does not allow it, and the dark fills were previously light pinks
 * and yellows that measured 1.7:1 under a white label.
 *
 * Light mode could nearly reuse the fills, but `warning` lands at 4.45:1 on the
 * page, so these are set outright rather than derived.
 *
 *     light, on white / on the page      dark, on the card
 *     error    #8e0b21  9.46 / 8.38      #fca5a5   9.17
 *     success  #015c45  8.02 / 7.10      #4ade80   9.99
 *     warning  #8a3f07  7.51 / 6.65      #fbbf24  10.43
 *     info     #005bd3  6.11 / 5.41      #7cc0ff   8.98
 */
const STATUS_INK = {
  light: {
    successText: '#015c45',
    warningText: '#8a3f07',
    errorText: '#8e0b21',
    infoText: '#005bd3',
  },
  dark: {
    successText: '#4ade80',
    warningText: '#fbbf24',
    errorText: '#fca5a5',
    infoText: '#7cc0ff',
  },
} as const;

type Palette = {
  accent: string;
  /**
   * The accent as INK, which in dark mode is not the same colour as the fill.
   *
   * The lifted forest `#1f7a55` carries a white label at 5.29:1, so it is a
   * sound fill. As text on the dark card it measures 2.50:1 and is unreadable,
   * and one token was being asked to do both jobs. Light mode has no such
   * problem and both roles stay `#16593f`.
   */
  accentText: string;
  /**
   * The sidebar's own ground, which FOLLOWS the theme: white in light mode,
   * a panel one step above the page in dark.
   *
   * It was near-black in both for a while, on the theory that a dark column
   * is the strongest cue that chrome is chrome. Measured, that held; looked
   * at, a black slab down the left of a white page was the loudest thing on
   * screen and competed with the work. Its roles stay separate from the page
   * palette so hovers, dividers and the selected row can be tuned for the
   * sider's ground rather than borrowed from a card's.
   */
  navBg: string;
  navFg: string;
  navFgMuted: string;
  navSelBg: string;
  navSelFg: string;
  /**
   * The 2px mark on the inside edge of the selected nav row.
   *
   * Not ornament. The selected row's fill measures 1.32:1 against the sidebar,
   * and nothing dark enough to still look like a dark sidebar gets near the 3:1
   * WCAG 1.4.11 asks of an indicator - `#616161` reaches only 2.81:1 and by
   * then the sidebar has stopped being dark. The marker is at 6.68:1 and is
   * what actually carries the state; it also means selection is not signalled
   * by colour alone.
   */
  navMarker: string;
  /** Rules ON the sider: the brand band's underline, the settings footer. */
  navDivider: string;
  bg: string;
  surface: string;
  elevated: string;
  /** Containers and dividers. Decorative, so it is allowed to be faint. */
  border: string;
  /**
   * The edge of anything you can type in or press.
   *
   * Separate and darker, because WCAG 1.4.11 asks 3:1 of the boundary that
   * identifies a control and the container hairline measures 1.24:1 against
   * white — which is why a row of input boxes read as ghosts. Cards and table
   * rules are exempt from that rule: losing sight of them costs nothing you
   * cannot infer from the layout.
   *
   * This reverses an earlier "one hairline for everything" decision. That was
   * right about consistency and wrong about legibility, and legibility wins on
   * a screen someone keys invoices into all day.
   */
  borderStrong: string;
  text: string;
  textSecondary: string;
  textTertiary: string;
  textQuaternary: string;
  /*
   * Placeholder text: lighter than a value so the two are not confused, but
   * never the disabled grey - a placeholder is instruction. Held at 4.5:1.
   */
  textPlaceholder: string;
  fill: string;
  fillSecondary: string;
  fillTertiary: string;
  fillQuaternary: string;
  success: string;
  warning: string;
  error: string;
  /**
   * The status colours written as TEXT.
   *
   * Separate from the fills above for the same reason `accentText` is separate
   * from `accent`: no single colour can both carry a white label and be legible
   * as text on a dark card.
   */
  successText: string;
  warningText: string;
  errorText: string;
  infoText: string;
  /** A tint of `error` for the ground under a destructive row on hover. */
  dangerSoft: string;
  /**
   * A deeper `error`, for the label ON that tint.
   *
   * `error` clears AA on the panel by a hair, so laying `dangerSoft` underneath
   * it would take the emphasis out of the legibility it was added for — the
   * hover has to deepen the ink as well as the ground.
   */
  dangerStrong: string;
  /** Explanatory surfaces. Deliberately not the brand — see `colorInfo`. */
  info: string;
  /** Focus ring, three pixels of translucent accent — the shadcn focus tell. */
  ring: string;
  shadowSm: string;
  shadowMd: string;
  /*
   * The button paint, after Polaris's `s-button` (admin-next): their fills,
   * size and press, with the edge stack softened. Their stack (a black lip,
   * a #b5b5b5 underline) is tuned for 8px corners; on a pill it read as a
   * thick second outline.
   *
   * There is no border. The edge is the inset shadow stack alone: a dark
   * bottom lip, a 1px rim, and a highlight half a pixel in. A border under it
   * was what drew the second ring - the fill shows through a transparent
   * border, so the eye saw the outer pixel AND the rim one pixel inside it.
   *
   * Dark mode has no Polaris original (the admin is light only), so the dark
   * set keeps the same three-layer structure with inks for a dark ground.
   */
  btnPrimaryPaint: string;
  btnPrimaryEdge: string;
  btnPrimaryInkHover: string;
  btnPrimaryInkActive: string;
  btnDefaultEdge: string;
  btnDangerEdge: string;
  btnDisabledBg: string;
  btnDisabledInk: string;
  btnPrimaryDisabledBg: string;
  btnPrimaryDisabledInk: string;
  /*
   * A card is two cards: a grey SHELL that carries the title, and a white
   * card inset 4px inside it that carries the content - the reference admin's
   * "card inside card". Both edges are shadows rather than borders, so they
   * can be as faint as the reference's (measured off it: a 1px edge near
   * #e7e7e7, lighter on top, with a short soft fall-off underneath).
   */
  cardShell: string;
  cardShellEdge: string;
  cardEdge: string;
  /*
   * One level deeper than a shell, for a group INSIDE a section (a line of a
   * script). Told apart by tone alone and drawn with no edge: three nested
   * boxes in one grey with one edge each read as noise, while ground -> shell
   * -> tray -> white card is four steps the eye can count.
   */
  cardTray: string;
  /*
   * A picker popup as a card inside a card: the shell carries the list's name
   * and the keys, the white card inside carries the rows. Its own pair rather
   * than cardShell, because a popup floats over the page and needs an opaque
   * ground - and in dark mode the inner card has to be the LIGHTER of the two
   * to read as raised, which the page's translucent shell cannot give.
   */
  popupShell: string;
  popupCard: string;
};

const PALETTE: Record<ThemeMode, Palette> = {
  light: {
    accent: ACCENT.light,
    accentText: ACCENT.light,

    /*
     * The page is one step off white and panels are pure white.
     *
     * At zinc[50] the page and a white card differed by 1.04:1, which is under
     * the step at which an edge registers at all; the card was being held
     * together by its hairline alone. #f1f1f1 doubles that to 1.14:1 and is
     * what lets a card read as an object on a screen that is mostly table.
     */
    /*
     * White, the work's own ground - the frame around it is the grey (navBg).
     * The reference draws its admin this way: a grey shell, the page on a
     * white card. It also gives the section shells (#f7f7f7) and the trays
     * inside them (#efefef) a clean white to step down from.
     */
    bg: neutral[0],
    surface: neutral[0],
    elevated: neutral[0],
    border: neutral[200],
    /* 4.83:1 on white. Essential control borders only - see `borderStrong`. */
    /* Polaris's input edge. 3.45:1 on white, 3.30:1 on the ground. */
    borderStrong: '#8a8a8a',

    /*
     * Text roles, each measured against white:
     *
     *                        on white   on the page
     *     text        #27272a   14.89:1       13.20:1
     *     secondary   #52525b    7.73:1        6.85:1
     *     tertiary    #5c5c65    6.62:1        5.87:1
     *     quaternary  #8a8a94    3.42:1        3.03:1   disabled only
     *
     * BOTH grounds, because muted text outside a card sits on the page and the
     * page is not white. Anything that carries meaning is at or above 4.5:1 on
     * either. Only `quaternary`, which antd uses for disabled controls, is
     * allowed to fall below it.
     */
    text: neutral[900],
    textSecondary: neutral[700],
    textTertiary: neutral[675],
    textQuaternary: neutral[500],
    textPlaceholder: neutral[600],

    fill: neutral[300],
    fillSecondary: neutral[200],
    fillTertiary: neutral[100],
    fillQuaternary: neutral[50],

    /*
     * White, like a card, with the page a step behind it. The selected row is
     * a quiet grey pill and its label the page ink at weight; the marker is
     * the brand itself, which carries 8.28:1 on white.
     */
    /* The frame and the sidebar on it: the reference's grey, so the white
       content card is the brightest thing on screen. */
    navBg: '#ebebeb',
    navFg: neutral[900],
    navFgMuted: neutral[700],
    /* On a grey rail the page you are on is the white tile, like the card. */
    navSelBg: neutral[0],
    navSelFg: neutral[900],
    navMarker: ACCENT.light,
    navDivider: '#dcdcdc',

    ...STATUS,
    ...STATUS_INK.light,
    dangerSoft: 'rgba(199, 10, 36, 0.08)',
    dangerStrong: '#8e0b21',
    ring: 'rgba(22, 89, 63, 0.22)',
    /* One pixel, no blur. Polaris lifts a card with an edge, not a cloud. */
    shadowSm: '0 1px 0 0 rgb(26 26 26 / 0.07)',
    shadowMd: '0 4px 6px -2px rgb(26 26 26 / 0.20)',
    btnPrimaryPaint: 'linear-gradient(180deg, rgb(255 255 255 / 0.07), rgb(255 255 255 / 0) 60%)',
    btnPrimaryEdge: 'inset 0 1px 0 0 rgb(255 255 255 / 0.10)',
    btnPrimaryInkHover: '#e3e3e3',
    btnPrimaryInkActive: '#cccccc',
    btnDefaultEdge: 'inset 0 0 0 1px rgb(0 0 0 / 0.08), 0 1px 1px 0 rgb(0 0 0 / 0.04)',
    btnDangerEdge: 'inset 0 1px 0 0 rgb(255 255 255 / 0.14)',
    btnDisabledBg: 'rgb(0 0 0 / 0.05)',
    btnDisabledInk: '#a8a8a8',
    btnPrimaryDisabledBg: 'rgb(0 0 0 / 0.05)',
    btnPrimaryDisabledInk: '#a8a8a8',
    cardShell: '#f7f7f7',
    cardShellEdge:
      '0 0 0 1px rgb(0 0 0 / 0.07), 0 1px 2px 0 rgb(0 0 0 / 0.05), 0 2px 6px 0 rgb(0 0 0 / 0.02)',
    cardEdge: '0 0 0 1px rgb(0 0 0 / 0.06), 0 1px 1px 0 rgb(0 0 0 / 0.04)',
    cardTray: '#efefef',
    popupShell: '#f7f7f7',
    popupCard: neutral[0],
  },
  dark: {
    accent: ACCENT.dark,
    /* Sage, the logo's light arm. 6.68:1 on the dark card, where the fill
       colour measures 2.50:1 and cannot be used for text. */
    accentText: BRAND.sage,

    /*
     * A near-black dark, not Polaris's #1a1a1a page.
     *
     * Polaris's dark end is tuned for a surface that sits in a bright browser
     * chrome; dropped into this product it turned the whole panel grey. The
     * relationships are kept - page below card, card below popover - and only
     * the depth changes:
     *
     *     sider    #09090b
     *     page     #0f0f12   1.04:1 above the sider
     *     card     #1a1a1e   1.10:1 above the page
     *     popover  #242429   1.12:1 above the card
     *
     * Those steps are small by design. A card is told apart by its border and
     * its shadow; the tonal step is there to stop the two reading as one sheet,
     * not to do the separating on its own.
     */
    bg: neutral[975],
    surface: neutral[950],
    elevated: neutral[925],
    border: neutral[800],
    /* 3.59:1 on the dark card. The SAME step as light mode - a cool grey at
       this lightness happens to clear 3:1 against both ends. */
    borderStrong: neutral[600],

    /*
     * Measured against the dark card #1a1a1a:
     *
     *     text        #e4e4e7  13.67:1
     *     secondary   #a1a1aa   6.77:1
     *     tertiary    #8a8a94   5.08:1
     *     quaternary  #52525b   2.17:1   disabled only
     *
     * Every one of these is better than the lighter dark it replaces, which is
     * the part worth noticing: going darker cost nothing in legibility.
     */
    text: neutral[200],
    textSecondary: neutral[400],
    textTertiary: neutral[500],
    textQuaternary: neutral[700],
    textPlaceholder: neutral[500],

    fill: neutral[700],
    fillSecondary: neutral[800],
    /*
     * Hover fills are TRANSLUCENT in dark mode, so they lift from whatever
     * they sit on.
     *
     * They were #242429 - the opaque step above a card - which is exactly the
     * raised surface every popover, dropdown, drawer and modal is drawn on. A
     * hover there painted the panel its own colour: the flyout, the account
     * menu and the availability panel's actions all showed no hover at all.
     * An opaque fill can be right on the card or on the panel, never both; a
     * white at 7% is a visible step on each.
     *
     * Comma syntax on purpose: antd parses these through its own colour
     * utilities to derive other tokens, and those do not read the CSS4 space
     * form.
     */
    fillTertiary: 'rgba(255, 255, 255, 0.07)',
    fillQuaternary: 'rgba(255, 255, 255, 0.04)',

    /*
     * A step ABOVE the page, not below it.
     *
     * It was the darkest surface on screen, and against a near-black page
     * that read as a hole down the left edge rather than as a panel. The
     * original sidebar sat a shade lighter than the page - the reference the
     * design was taken from does the same - and that is what makes it read as
     * chrome. The selected row then steps up again from it.
     */
    /*
     * Near the page, not above it. #242429 read as a grey slab beside a
     * near-black card, and the frame should recede so the card leads. A step
     * lighter than the page still lets the card's edge show against it.
     */
    navBg: neutral[960],
    navFg: neutral[200],
    navFgMuted: neutral[400],
    navSelBg: neutral[900],
    navSelFg: neutral[0],
    navMarker: BRAND.sage,
    /* A white alpha, because a palette grey one step up from #242429 is too
       close to tell from the ground it is drawn on. */
    navDivider: 'rgb(255 255 255 / 0.10)',

    ...STATUS,
    ...STATUS_INK.dark,
    dangerSoft: 'rgba(252, 165, 165, 0.16)',
    dangerStrong: '#ffb3bd',
    ring: 'rgba(110, 175, 118, 0.40)',
    shadowSm: '0 1px 0 0 rgb(0 0 0 / 0.6)',
    shadowMd: '0 4px 6px -2px rgb(0 0 0 / 0.7)',
    /* The primary is white here, so the gradient darkens its foot instead. */
    btnPrimaryPaint: 'linear-gradient(180deg, rgb(0 0 0 / 0), rgb(0 0 0 / 0.05))',
    btnPrimaryEdge: 'none',
    btnPrimaryInkHover: neutral[950],
    btnPrimaryInkActive: neutral[900],
    btnDefaultEdge: 'inset 0 0 0 1px rgb(255 255 255 / 0.10), inset 0 1px 0 0 rgb(255 255 255 / 0.04)',
    btnDangerEdge: 'inset 0 1px 0 0 rgb(255 255 255 / 0.12)',
    btnDisabledBg: 'rgb(255 255 255 / 0.06)',
    btnDisabledInk: 'rgb(255 255 255 / 0.30)',
    btnPrimaryDisabledBg: 'rgb(255 255 255 / 0.06)',
    btnPrimaryDisabledInk: 'rgb(255 255 255 / 0.30)',
    cardShell: 'rgb(255 255 255 / 0.025)',
    cardShellEdge: '0 0 0 1px rgb(255 255 255 / 0.06), 0 1px 2px 0 rgb(0 0 0 / 0.5)',
    cardEdge: '0 0 0 1px rgb(255 255 255 / 0.07)',
    cardTray: 'rgb(255 255 255 / 0.05)',
    popupShell: neutral[950],
    popupCard: neutral[925],
  },
};

/**
 * Everything the Tailwind bridge in `theme-provider` republishes as CSS
 * variables, so a `bg-surface` div and an antd Card cannot drift apart. Exported
 * so there is exactly one place these values are written.
 */
export function palette(mode: ThemeMode): Palette {
  return PALETTE[mode];
}

/**
 * Seed tokens.
 *
 * `controlHeight: 32` is the compact default — shadcn's `h-8`. The counter sale
 * screen asks for `size="large"` on the controls a pharmacist touches while
 * holding a strip of tablets, and antd derives that as 40px, so the one screen
 * that needs a real touch target still gets one without inflating every table
 * filter and drawer field to match it.
 *
 * Deliberately NOT the admin panel's 30/36/44 scale. That app is read; this one
 * is keyed, and its two grids are laid out against a 32px row — the colour and
 * the type are shared between the two products, the density is not.
 */
function seedTokens(mode: ThemeMode): ThemeConfig['token'] {
  const p = PALETTE[mode];

  return {
    colorPrimary: p.accent,
    /*
     * Blue, not the brand.
     *
     * antd derives every informational surface from `colorInfo`, and pointing it
     * at the accent made each explanatory banner a green panel — which on a
     * screen whose only other green is the primary button reads as something to
     * press. An explanation is not an action, so it gets a colour that is not
     * used for one anywhere else in the product.
     */
    /*
     * A link is TEXT, so it takes the ink accent rather than `colorPrimary`.
     *
     * The two are the same colour in light mode and deliberately different in
     * dark, where the fill green measures 2.50:1 as text. antd defaults
     * `colorLink` to `colorPrimary`, which is how that failure would otherwise
     * have reached every link in the product.
     */
    colorLink: p.accentText,
    colorLinkHover: p.accentText,
    colorLinkActive: p.accentText,

    colorInfo: p.info,
    colorSuccess: p.success,
    colorWarning: p.warning,
    colorError: p.error,

    /*
     * antd derives its text variants from the base colour, which puts a dark
     * fill colour on a dark card. Set outright instead - see `STATUS_INK`.
     */
    colorSuccessText: p.successText,
    colorWarningText: p.warningText,
    colorErrorText: p.errorText,
    colorInfoText: p.infoText,

    colorBgBase: p.bg,
    colorTextBase: p.text,

    colorBgLayout: p.bg,
    colorBgContainer: p.surface,
    colorBgElevated: p.elevated,
    colorBgSpotlight: mode === 'dark' ? neutral[800] : neutral[900],

    colorText: p.text,
    colorTextSecondary: p.textSecondary,
    colorTextTertiary: p.textTertiary,
    colorTextQuaternary: p.textQuaternary,

    /*
     * antd points both of these at `colorTextQuaternary` by default, which is
     * the disabled-control grey. A placeholder is instruction - "Type or click
     * to browse stock" is how someone learns what the field does - so it stays
     * readable. It was the tertiary grey (6.62:1) and read as a value: "BATCH
     * NO." in a box looked filled in. Its own grey sits between the two, 4.83:1
     * on a white card.
     */
    colorTextPlaceholder: p.textPlaceholder,
    colorTextDescription: p.textSecondary,

    /*
     * antd already splits these the way they need splitting: `colorBorder` is
     * the edge of a control, `colorBorderSecondary` the edge of a container.
     * They were collapsed to one value for visual consistency, which made every
     * input as faint as a divider. The control edge carries the contrast; the
     * container edge stays quiet.
     */
    colorBorder: p.borderStrong,
    colorBorderSecondary: p.border,

    colorFill: p.fill,
    colorFillSecondary: p.fillSecondary,
    colorFillTertiary: p.fillTertiary,
    colorFillQuaternary: p.fillQuaternary,

    /*
     * Two radii assigned by role: 8 on anything you press or type in, 12 on
     * anything that is a container. A card is a bigger object than a button and
     * reads wrong at the same curve. 4 is left for the small inner parts -
     * badges, checkboxes, the count pill on a tab.
     */
    borderRadius: 8,
    borderRadiusLG: 12,
    borderRadiusSM: 4,
    borderRadiusXS: 2,

    fontFamily: FONT.body,
    /*
     * 13px body, which is Polaris's `font-size-325` and one step below antd's
     * default. On a product made almost entirely of tables it is the single
     * change that buys the most: a fourth column fits in the width three used
     * to take.
     *
     * Heading 1 is 34px and exists for ONE thing - the figure that leads a
     * screen, set in the display face. At 2.6x the body it is what satisfies
     * the house rule that display type is at least 2.5x body; the 20px page
     * title at Heading 3 does not, and is not meant to. A page title says where
     * you are. The number is the content.
     */
    fontSize: 13,
    fontSizeLG: 14,
    fontSizeSM: 12,
    fontSizeHeading1: 34,
    fontSizeHeading2: 24,
    fontSizeHeading3: 20,
    fontSizeHeading4: 16,
    fontSizeHeading5: 13,
    lineHeight: 1.54,

    controlHeight: 32,

    /*
     * Nearly flat. shadcn separates surfaces with a border and reserves shadow
     * for things that genuinely float — a dropdown, a popover, a drawer.
     */
    boxShadow: p.shadowMd,
    boxShadowSecondary: p.shadowMd,
    boxShadowTertiary: p.shadowSm,

    /*
     * Short enough to feel like a response rather than an animation. Motion is
     * not switched off wholesale — antd's `motion: false` seed would also kill
     * the fade on drawers and dropdowns, where an instant appearance reads as a
     * glitch — but nothing in the chrome should take longer to settle than the
     * hand takes to move on.
     */
    motionDurationFast: '0.05s',
    motionDurationMid: '0.08s',
    motionDurationSlow: '0.12s',

    wireframe: false,
  };
}

/**
 * Component overrides. Each one exists to close a specific gap between antd's
 * defaults — tuned for a roomier, more colourful interface — and the compact,
 * near-monochrome one this app wants.
 */
function componentTokens(mode: ThemeMode): ThemeConfig['components'] {
  const p = PALETTE[mode];

  return {
    Layout: {
      bodyBg: p.bg,
      headerBg: p.surface,
      /*
       * The sider's own ground, from the palette - see `navBg` there for why
       * it follows the theme rather than staying dark.
       */
      siderBg: p.navBg,
      headerHeight: LAYOUT.headerHeight,
      headerPadding: '0 14px',
    },

    /*
     * The selected item is a neutral fill, not a tinted one. A coloured
     * selection in the sidebar competes with the accent on the primary button,
     * and in a screen full of status tags the only thing that should be teal is
     * the action you are about to take.
     */
    /*
     * The sidebar sits on ONE left rail at `NAV.gutter`, and three different
     * mechanisms have to agree on it:
     *
     *   group title  padding-inline, from the global `padding` token   → 16
     *   item content itemMarginInline + inlineIndent                   → 8 + 8
     *   the logo     the brand row's own padding in AppLayout          → 16
     *
     * `inlineIndent` is the one that bites. rc-menu writes it as an INLINE
     * `padding-left` (`useDirectionStyle`), so it beats `itemPaddingInline` and
     * no stylesheet can win against it — and because an item inside a group
     * counts as one level deep, antd's default of 24 pushed every icon 24px to
     * the right of its own group heading. It is set to 8 on the <Menu> itself.
     */
    Menu: {
      itemHeight: 36,
      itemBorderRadius: 6,
      itemMarginInline: NAV.railInset,
      itemMarginBlock: 2,
      itemPaddingInline: NAV.railInset,
      iconMarginInlineEnd: 10,
      iconSize: 16,
      fontSize: 13,
      groupTitleFontSize: 11,
      /*
       * Every colour below is measured against the sider's own ground, which
       * follows the theme (see `navBg` in the palette):
       *
       *     resting label   #b5b5b5 on #1a1a1a   8.49:1
       *     hover label     #e3e3e3 on #1a1a1a  13.56:1
       *     selected label  #ffffff on #303030  13.20:1
       *
       * Hover and press are white washes rather than palette fills, so they
       * stay correct whichever theme supplies the sider colour.
       */
      groupTitleColor: p.navFgMuted,
      itemBg: 'transparent',
      subMenuItemBg: 'transparent',
      itemColor: p.navFgMuted,
      itemHoverBg: p.fillTertiary,
      itemHoverColor: p.navFg,
      /*
       * The pressed state, and the one that was wrong.
       *
       * `itemActiveBg` defaults to `controlItemBgActive`, which antd derives
       * from the PRIMARY colour — so every click on a nav row flashed a
       * green-tinted block, the single loudest thing in the chrome, on a
       * sidebar whose whole design is that colour lives on the button and
       * nowhere else. A press is a neutral step darker than hover, no more.
       */
      itemActiveBg: p.fillSecondary,
      /*
       * Selected is a lighter row, a heavier label, and a 2px marker added in
       * `styles.css`. Three signals, because the fill alone cannot be one.
       *
       * The fill measures 1.32:1 against the sider, and nothing dark enough to
       * still look like a dark sidebar gets near the 3:1 WCAG 1.4.11 asks of an
       * indicator - #616161 reaches only 2.81:1 and by then it is not a dark
       * sidebar any more. So the marker carries the state, at 6.68:1, and the
       * fill is left to do what it is actually good at, which is making the row
       * feel picked up.
       *
       * The marker lives in CSS because antd has no token for it, and it is a
       * pseudo-element rather than a border so the item does not grow by 2px
       * and shift every row below it on selection.
       */
      itemSelectedBg: p.navSelBg,
      itemSelectedColor: p.navSelFg,
      activeBarWidth: 0,
      collapsedWidth: LAYOUT.siderCollapsedWidth,

      /*
       * antd substitutes a whole parallel set of tokens when a Menu is themed
       * `dark`, and ignores every one of the light values above — which is why
       * the selected item rendered as a solid `colorPrimary` bar on a navy
       * `#001529` ground regardless of what was configured. Each one is
       * restated here against the same palette.
       */
      darkItemBg: 'transparent',
      darkSubMenuItemBg: 'transparent',
      darkPopupBg: p.elevated,
      darkItemColor: p.navFgMuted,
      darkItemHoverBg: p.fillTertiary,
      darkItemHoverColor: p.navFg,
      darkItemSelectedBg: p.navSelBg,
      darkItemSelectedColor: p.navSelFg,
      darkGroupTitleColor: p.navFgMuted,
    },

    Button: {
      /*
       * OPTION A - the primary button is near-black, and the brand green is
       * spent on links, selection, the focus ring and the nav marker instead.
       *
       * This is the one deliberate departure from "green is the colour of every
       * primary action". The reason is that this product has amber and red
       * doing real work on every screen - expiring, expired, out of stock - and
       * a saturated green button competes with exactly the signals an operator
       * must not miss. Holding colour back for meaning is most of why the
       * reference admin reads as calm.
       *
       * `colorPrimary` stays the brand GLOBALLY; only the button fill is
       * overridden here. That is what keeps the focus ring, the selected row
       * and every link green while the button is not.
       *
       * To switch to Option B, delete the four lines below and the button
       * inherits the brand again. Nothing else needs to change.
       */
      /*
       * Light: a step under Polaris's #303030. Their heavy black lip is what
       * makes #303030 read near-black; with the softer edge it read grey.
       */
      colorPrimary: mode === 'dark' ? neutral[0] : '#222222',
      colorPrimaryHover: mode === 'dark' ? neutral[100] : '#1a1a1a',
      colorPrimaryActive: mode === 'dark' ? neutral[200] : '#111111',
      primaryColor: mode === 'dark' ? neutral[950] : neutral[0],

      /*
       * PILL buttons, on trial. Every button, every size, from these three
       * lines - so reverting is deleting them. Inputs and selects keep their
       * 8px corners, which is what lets a button read as the thing you press
       * among the things you type in.
       */
      borderRadius: 999,
      borderRadiusLG: 999,
      borderRadiusSM: 999,

      /*
       * Polaris `size-base`: 28px tall from 6px + a 16px line + 6px, 12px
       * label at 550, 12px either side. Every button at the default size is
       * one height. Inputs stay 32; a button joined to one is lifted back to
       * 32 in styles.css so the pair still lines up. The label is set in
       * Inter, Polaris's face, in styles.css.
       */
      controlHeight: 28,
      fontWeight: 550,
      contentFontSize: 12,
      contentLineHeight: 16 / 12,
      contentFontSizeSM: 12,
      paddingInline: 12,
      paddingInlineSM: 8,
      paddingInlineLG: 16,
      /* antd's shadows off: the Polaris edge in styles.css replaces them. */
      primaryShadow: 'none',
      defaultShadow: 'none',
      dangerShadow: 'none',
      /* Light: Polaris's white, #fafafa hover, #f7f7f7 press, #303030 ink. */
      defaultBg: p.surface,
      defaultBorderColor: p.border,
      defaultColor: mode === 'dark' ? p.text : '#303030',
      defaultHoverBg: mode === 'dark' ? neutral[925] : '#fafafa',
      defaultHoverColor: mode === 'dark' ? p.text : '#303030',
      defaultActiveBg: mode === 'dark' ? neutral[950] : '#f7f7f7',
      defaultActiveColor: mode === 'dark' ? p.text : '#303030',
      /* Light: Polaris's critical, #c70a24 / #a30a24 / #8e0b21. */
      ...(mode === 'light'
        ? { colorError: '#c70a24', colorErrorHover: '#a30a24', colorErrorActive: '#8e0b21' }
        : {}),
      textHoverBg: p.fillTertiary,
    },

    /*
     * The title is on the grey shell, so it is a quiet 13px label row rather
     * than a 46px header with a rule under it. See "cards" in styles.css.
     */
    Card: {
      bodyPadding: 16,
      bodyPaddingSM: 12,
      headerPadding: 12,
      headerPaddingSM: 12,
      headerHeight: 36,
      headerHeightSM: 36,
      headerFontSize: 13,
      headerFontSizeSM: 13,
      headerBg: 'transparent',
      boxShadowTertiary: 'none',
    },

    /*
     * Header is a plain row with a rule under it — no fill, no rounded corners,
     * no vertical splitters. Rows are 34px tall against antd's 46.
     */
    Table: {
      headerBg: 'transparent',
      headerColor: p.textSecondary,
      headerSplitColor: 'transparent',
      headerBorderRadius: 0,
      borderColor: p.border,
      /*
       * Roomier than the admin panel on purpose. Rows here are read with one
       * hand on a keyboard and often on a tablet at arm's length across a
       * counter, where the admin's 7px rows are a target problem rather than a
       * density win.
       */
      cellPaddingBlock: 10,
      cellPaddingInline: 14,
      cellPaddingBlockSM: 8,
      cellPaddingInlineSM: 12,
      cellFontSize: 14,
      cellFontSizeSM: 13,
      /*
       * A step under a default Tag's fill (fillTertiary). At the same grey, a
       * hovered row swallowed its tags - "Billed" and "Counter" lost their
       * pill the moment the pointer crossed them.
       */
      rowHoverBg: p.fillQuaternary,
      footerBg: 'transparent',
      stickyScrollBarBg: p.fill,
    },

    Input: {
      paddingInline: 10,
      paddingBlock: 4,
      activeBorderColor: p.accent,
      hoverBorderColor: mode === 'dark' ? neutral[600] : neutral[500],
      activeShadow: `0 0 0 3px ${p.ring}`,
      errorActiveShadow: `0 0 0 3px ${p.error}33`,
      addonBg: p.fillTertiary,
    },

    InputNumber: {
      paddingInline: 10,
      activeBorderColor: p.accent,
      hoverBorderColor: mode === 'dark' ? neutral[600] : neutral[500],
      activeShadow: `0 0 0 3px ${p.ring}`,
    },

    /*
     * Both option backgrounds are stated rather than inherited. antd derives
     * `optionSelectedBg` from `controlItemBgActive` and `optionActiveBg` from
     * `controlItemBgHover` — the same primary-tinted aliases that turned the
     * sidebar's pressed state into a green block. In a dropdown of medicine
     * names those tints are worse than merely loud: the row under the cursor is
     * the one being read, and a saturated fill behind two lines of small grey
     * text is where a wrong strength gets picked.
     *
     * Hover is the lighter of the two and selection the heavier, so a list you
     * are moving through never looks like a list with something chosen in it.
     */
    Select: {
      optionActiveBg: p.fillTertiary,
      optionSelectedBg: p.fillSecondary,
      optionSelectedColor: p.text,
      optionSelectedFontWeight: 600,
      optionPadding: '5px 10px',
      activeBorderColor: p.accent,
      hoverBorderColor: mode === 'dark' ? neutral[600] : neutral[500],
      activeOutlineColor: p.ring,
      selectorBg: p.surface,
      /* The clear cross is an action, so it darkens on approach. */
      clearBg: p.surface,
      multipleItemBg: p.fillTertiary,
    },

    DatePicker: {
      activeBorderColor: p.accent,
      hoverBorderColor: mode === 'dark' ? neutral[600] : neutral[500],
      activeShadow: `0 0 0 3px ${p.ring}`,
      cellActiveWithRangeBg: p.fillSecondary,
    },

    Form: {
      itemMarginBottom: 16,
      verticalLabelPadding: '0 0 5px',
      labelFontSize: 13,
      labelColor: p.text,
      labelRequiredMarkColor: p.error,
    },

    /* A tag is a shadcn badge: small, quiet, and never the loudest thing in a row. */
    Tag: {
      defaultBg: p.fillTertiary,
      defaultColor: p.textSecondary,
      borderRadiusSM: 5,
      fontSizeSM: 12,
      lineHeightSM: 1.5,
    },

    Alert: {
      defaultPadding: '9px 12px',
      withDescriptionPadding: '12px 16px',
      withDescriptionIconSize: 18,
    },

    /*
     * A card inside a card, like the sections and fields: the track is the
     * grey shell, the option that is on is the white card with the soft edge.
     */
    Segmented: {
      trackBg: p.cardShell,
      trackPadding: 3,
      itemSelectedBg: p.surface,
      itemSelectedColor: p.text,
      itemColor: p.textSecondary,
      itemHoverBg: p.fillTertiary,
      boxShadowTertiary: p.cardEdge,
      borderRadius: 10,
      borderRadiusSM: 8,
    },

    /*
     * Modal, drawer, dropdown, popover and every popup list share one look:
     * the availability panel's. 12px corners, a hairline edge, the elevated
     * surface, a 14px title. The structure - ruled header, ruled footer, 16px
     * body - is in `styles.css` under "overlays", because antd has no tokens
     * for where its own rules go.
     */
    Modal: {
      titleFontSize: 14,
      headerBg: p.elevated,
      contentBg: p.elevated,
      footerBg: p.elevated,
      borderRadiusLG: 12,
    },

    Drawer: { paddingLG: 20, footerPaddingBlock: 12, footerPaddingInline: 20 },

    Dropdown: { paddingBlock: 4, controlItemBgHover: p.fillTertiary, borderRadiusLG: 12 },

    /*
     * Tooltips follow the theme: white in light mode, near-black in dark. A
     * white chip lands on white cards, so it carries the panel's hairline and
     * shadow to keep an edge (see "tooltips" in styles.css). Toasts stay
     * near-black in both themes - they announce, and announcing wants the
     * contrast.
     */
    Tooltip: {
      borderRadius: 8,
      controlHeight: 28,
      /* White in light mode, a raised chip like every other panel; near-black
         in dark, where a white chip would be the brightest thing on screen. */
      colorBgSpotlight: mode === 'dark' ? neutral[950] : neutral[0],
      colorTextLightSolid: mode === 'dark' ? neutral[50] : neutral[900],
    },

    /*
     * The toast's ground and shape. Its text and icon colours are in
     * `styles.css`: antd 6 does not read colour aliases from a Message
     * component token, so `colorText` set here was silently ignored and the
     * toast printed dark grey on near-black in light mode.
     */
    Message: {
      contentBg: neutral[950],
      contentPadding: '9px 14px',
      borderRadiusLG: 10,
    },

    Descriptions: { itemPaddingBottom: 12, labelBg: 'transparent', labelColor: p.textSecondary },

    Statistic: { contentFontSize: 24, titleFontSize: 12 },

    Popover: { borderRadiusLG: 12, titleMinWidth: 160 },

    Skeleton: { gradientFromColor: p.fillTertiary, gradientToColor: p.fillSecondary },

    Switch: { trackMinWidth: 38, trackHeight: 20, handleSize: 16 },

    Divider: { marginLG: 16 },

    Empty: { controlHeightLG: 40 },

    Result: { titleFontSize: 18, subtitleFontSize: 14 },

    Avatar: { borderRadius: 6 },

    Spin: { dotSize: 18 },
  };
}

export function themeConfig(mode: ThemeMode): ThemeConfig {
  return {
    algorithm: mode === 'dark' ? theme.darkAlgorithm : theme.defaultAlgorithm,
    token: seedTokens(mode),
    components: componentTokens(mode),
    /*
     * CSS variables mean a mode switch re-paints from the same stylesheet
     * rather than regenerating every component's styles — and it lets the
     * Tailwind bridge in ThemeProvider read live values.
     */
    cssVar: { key: 'tlp' },
    hashed: true,
  };
}
